import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserRole, Route, RolePermissions } from '@/types/roles';
import { routesByRole, rolePermissions } from '@/config/routes';
import { useAuth } from '@/components/AuthProvider';

interface RoleContextType {
  currentRole: UserRole;
  setRole: (role: UserRole) => void;
  availableRoutes: Route[];
  permissions: RolePermissions;
  isLoading: boolean;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentRole, setCurrentRole] = useState<UserRole>(UserRole.CLIENT);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role) {
      // Use the role from the user's profile in the database
      const dbRole = user.role as UserRole;
      if (Object.values(UserRole).includes(dbRole)) {
        setCurrentRole(dbRole);
        localStorage.setItem('selectedRole', dbRole);
      }
    } else {
      // Fallback to saved role from localStorage only if no user role exists
      const savedRole = localStorage.getItem('selectedRole') as UserRole;
      if (savedRole && Object.values(UserRole).includes(savedRole)) {
        setCurrentRole(savedRole);
      } else {
        setCurrentRole(UserRole.CLIENT);
        localStorage.setItem('selectedRole', UserRole.CLIENT);
      }
    }
    setIsLoading(false);
  }, [user]);

  const setRole = (role: UserRole) => {
    setCurrentRole(role);
    localStorage.setItem('selectedRole', role);
    // Redirect to home when role changes
    window.location.href = '/';
  };

  const value = {
    currentRole,
    setRole,
    availableRoutes: routesByRole[currentRole],
    permissions: rolePermissions[currentRole],
    isLoading
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};